/**
 * SidePanel — slide-in panel from the right (or left) with unified
 * drag-to-dismiss gesture.
 *
 * Per spec: side panels support swipe-to-close from the edge they
 * originated from. Right-side panel → swipe right to close. Left-side
 * panel → swipe left to close.
 */
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { SPRING, DURATION, EASE } from "@/shared/motion/tokens";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";

export default function SidePanel({
  open,
  onClose,
  children,
  side = "right",       // 'right' | 'left'
  width = "min(88vw, 360px)",
  testid,
  className = "",
  ariaLabel,
  disableDragDismiss = false,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const initialX = side === "right" ? "100%" : "-100%";
  const sideClass = side === "right" ? "right-0" : "left-0";

  // Drag-to-dismiss — right panels close with a swipe RIGHT (origin
  // right), left panels close with a swipe LEFT.
  const scrollRef = useRef(null);
  const { motionProps, backdropOpacity } = useDragDismiss({
    onDismiss: onClose,
    origin: side,
    open,
    scrollRef,
    enabled: !disableDragDismiss,
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="sp-backdrop"
            className="fixed inset-0 z-[60] bg-black/45"
            style={{
              opacity: backdropOpacity,
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.out }}
            onClick={onClose}
            data-testid={testid ? `${testid}-backdrop` : undefined}
          />
          <motion.div
            key="sp-panel"
            className={`fixed top-0 bottom-0 ${sideClass} z-[61] ${className}`}
            style={{ width }}
            initial={{ x: initialX }}
            animate={{ x: 0 }}
            exit={{ x: initialX }}
            transition={SPRING.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={testid}
            {...(disableDragDismiss ? {} : motionProps)}
          >
            <div ref={scrollRef} className="h-full overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
